import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);  
  disableAddToBagButtonDialog() {
    const addToBagButtons = document.querySelectorAll('button[type="submit"][name="add"].add-to-cart-button, button.add-to-cart-button[type="submit"]');
    addToBagButtons.forEach(button => {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      
      // Hide text content
      const textContent = button.querySelector('.add-to-cart-text__content');
      if (textContent) {
        // Store original display value if not already stored
        if (!textContent.hasAttribute('data-original-display')) {
          const computedStyle = window.getComputedStyle(textContent);
          textContent.setAttribute('data-original-display', computedStyle.display || 'inline');
        }
        textContent.style.display = 'none';
      }
      
      // Create or show spinner
      let spinner = button.querySelector('.loading-spinner');
      if (!spinner) {
        spinner = document.createElement('span');
        spinner.className = 'loading-spinner';
        spinner.setAttribute('aria-hidden', 'true');
        const textWrapper = button.querySelector('.add-to-cart-text');
        if (textWrapper) {
          textWrapper.appendChild(spinner);
        } else {
          // Fallback: insert before button content
          button.insertBefore(spinner, button.firstChild);
        }
      }
      spinner.style.display = 'inline-block';
    });
  }

  // Enable Add to Bag button after all async data is loaded
  enableAddToBagButtonDialog() {
    const addToBagButtons = document.querySelectorAll('button[type="submit"][name="add"].add-to-cart-button, button.add-to-cart-button[type="submit"]');
    addToBagButtons.forEach(button => {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      
      // Show text content (restore original display value)
      const textContent = button.querySelector('.add-to-cart-text__content');
      if (textContent) {
        const originalDisplay = textContent.getAttribute('data-original-display') || 'inline';
        textContent.style.display = originalDisplay;
        textContent.removeAttribute('data-original-display');
      }
      
      // Hide spinner
      const spinner = button.querySelector('.loading-spinner');
      if (spinner) {
        spinner.style.display = 'none';
      }
    });
  }
  textBookFuncs(){
    const summaryElements = document.querySelectorAll('.accordion-variant-picker .details__header');
     
     const radioElements = document.querySelectorAll('input[type="radio"]');
     
     // Open the first accordion by default
     const activeAccordion = document.querySelector('.accordion-variant-picker[isActive="true"]');
     if (activeAccordion) {
       activeAccordion.open = true;
     } else {
       // Fallback to first accordion if no active one found
       const firstAccordion = document.querySelector('.accordion-variant-picker');
       if (firstAccordion) {
         firstAccordion.open = true;
       }
     }
    
    // Make checkboxes work independently - prevent any interference
    const checkboxes = document.querySelectorAll('.variant-checkbox');
    checkboxes.forEach(checkbox => {
      // Prevent checkbox click from bubbling up
      checkbox.addEventListener('click', function(e) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, true); // Use capture phase
      
      checkbox.addEventListener('change', function(e) {
        // Stop propagation to prevent variant picker from updating URL
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, true);
    });
    
    // Prevent accordion-variant-option from interfering with checkbox clicks
    const variantOptions = document.querySelectorAll('.accordion-variant-option');
    variantOptions.forEach(option => {
      option.addEventListener('click', function(e) {
        // Always stop propagation for checkbox-related elements
        if (e.target.closest('.purchase-checkbox-container') || 
            e.target.closest('.variant-checkbox') || 
            e.target.closest('.checkbox-label')) {
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
        
        // Also check if the click target is the checkbox or label directly
        if (e.target.type === 'checkbox' || e.target.classList.contains('checkbox-label')) {
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      }, true); // Use capture phase
    });
    
    // Override the variant picker's URL update logic to prevent URL changes for multi-select
    const variantPicker = document.querySelector('variant-picker[ref="mainVariantPicker"]');
    if (variantPicker && variantPicker.variantChanged) {
      const originalVariantChanged = variantPicker.variantChanged.bind(variantPicker);
      
      variantPicker.variantChanged = function(event) {
        // Check if the event came from a checkbox
        if (event.target && event.target.classList.contains('variant-checkbox')) {
          // Don't update URL for checkbox changes
          return;
        }
        
        // Prevent all URL updates for multi-select mode
        return false;
      };
    }
  }
  async updateRentalDatesDialog() {
    const rentalDateElements = document.querySelectorAll('.accordion-variant-rental-due-date[data-refresh-date]');
    
    if (rentalDateElements.length === 0) {
      // No rental dates to update, enable button immediately
      this.enableAddToBagButtonDialog();
      return;
    }

    // Try to get termId from various sources
    let termId = null;
    
    // Try window.sectionData first (from product display manager)
    if (window.sectionData?.courseInfo?.termId) {
      termId = window.sectionData.courseInfo.termId;
    }
    // Try URL parameter
    else if (window.location.search.includes('termId=')) {
      const urlParams = new URLSearchParams(window.location.search);
      termId = urlParams.get('termId');
    }
    // Try window.courseData.selectedTermId (from courses-materials page)
    else if (window.courseData?.selectedTermId) {
      termId = window.courseData.selectedTermId;
    }
    
    if (termId) {
      // If we have termId, fetch from ddcs_term
      try {
        const api_url = '/apps/shopifyData/metaobjects';
        const queryData = `fields.term_id:'${termId}' fields.disabled_flag:false`;
        const url = `${api_url}?type=ddcs_term&query=${encodeURIComponent(queryData)}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          this.enableAddToBagButtonDialog();
          return;
        }

        const result = await response.json();
        if (!result.success || !result.data || result.data.length === 0) {
          this.enableAddToBagButtonDialog();
          return;
        }

        const term = result.data.filter(item => item.status === "ACTIVE")[0];
        const rentalEndDate = term?.rental_end_date;
        
        if (!rentalEndDate) {
          this.enableAddToBagButtonDialog();
          return;
        }

        // Format date like html-renderer.js: MM/DD/YY
        const dateParts = rentalEndDate.split('-');
        const formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0].slice(-2)}`;

        // Update all rental date elements
        rentalDateElements.forEach((el, index) => {
          el.textContent = formattedDate;
          // Remove debug span if exists
          const debugSpan = el.querySelector('span[style="display:none"]');
          if (debugSpan) debugSpan.remove();
        });

        // Update all checkboxes with rental due date
        const checkboxes = document.querySelectorAll('.purchase-checkbox[data-book-rent-due-date]');
        checkboxes.forEach(checkbox => {
          checkbox.setAttribute('data-book-rent-due-date', formattedDate);
        });

        // Enable button after rental dates are updated
        this.enableAddToBagButtonDialog();

      } catch (error) {
        console.error('Error updating rental dates:', error);
        // Enable button even if there's an error
        this.enableAddToBagButtonDialog();
      }
    } else {      
      try {
        const api_url = '/apps/shopifyData/metaobjects';
        const rentalDates = [];
        try {
          // Step 1: Fetch all terms from ddcs_term 
          const termQuery = `fields.disabled_flag:false fields.is_term_open_flag:true`;
          const termUrl = `${api_url}?type=ddcs_term&query=${encodeURIComponent(termQuery)}`;
          
          const termResponse = await fetch(termUrl, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin'
          });

          if (!termResponse.ok) {
            console.error(`Failed to fetch ddcs_term `);
            return;
          }

          const termResult = await termResponse.json();
          if (!termResult.success || !termResult.data || termResult.data.length === 0) {
            return;
          }
          for (let index = 0; index < termResult.data.length; index++) {
            const term = termResult.data[index];
              
            const rentalEndDate = term?.rental_end_date;
            
            if (rentalEndDate) {
              rentalDates.push(rentalEndDate);
            }
            
          }
          
        } catch (error) {
          console.error(`Error fetching rental date:`, error);
        }

        // Find the earliest rental date
        if (rentalDates.length === 0) {
          this.enableAddToBagButtonDialog();
          return;
        }

        const latestDate = rentalDates.reduce((latest, current) => {
          return new Date(current) > new Date(latest) ? current : latest;
        });

        // Format date like html-renderer.js: MM/DD/YY
        const dateParts = latestDate.split('-');
        const formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
        const dateRent = `${dateParts[0]}-${dateParts[1]}-${dateParts[2]}`;
        // Update all rental date elements
        rentalDateElements.forEach((el, index) => {
          el.textContent = formattedDate;
          // Remove debug span if exists
          const debugSpan = el.querySelector('span[style="display:none"]');
          if (debugSpan) debugSpan.remove();
        });

        // Update all checkboxes with rental due date
        const checkboxes = document.querySelectorAll('.purchase-checkbox[data-book-rent-due-date]');
        checkboxes.forEach(checkbox => {
          checkbox.setAttribute('data-book-rent-due-date', dateRent);
        });

        // Enable button after rental dates are updated
        this.enableAddToBagButtonDialog();

      } catch (error) {
        console.error('Error updating rental dates:', error);
        // Enable button even if there's an error
        this.enableAddToBagButtonDialog();
      }
    }
  }
  setupRequirementsButtonsDialog() {
    const requirementsButtons = document.querySelectorAll('.requirements-link');
    
    requirementsButtons.forEach(button => {
      // Remove existing onclick to avoid conflicts
      button.onclick = null;
      
      // Add event listener
      button.addEventListener('click', function(e) {
        e.stopPropagation();
        
        const variantId = button.dataset.variantId;
        const productTitle = button.dataset.productTitle;
        
        //console.log('Requirements button clicked:', { variantId, productTitle });
        
        // Try to get variant data from embedded JSON
        const variantData = {
          available: true,
          id: 'gid://shopify/ProductVariant/' + variantId,
          metafields: []
        };
        
        // Extract metafields from button's data attributes
        const metafieldsArray = [];
        const dataAttributes = Array.from(button.attributes).filter(attr => attr.name.startsWith('data-mf-'));
        
        //console.log('Data attributes:', dataAttributes);
        
        // Group attributes by key (without 'data-mf-' prefix)
        const metafieldsObj = {};
        dataAttributes.forEach(attr => {
          const key = attr.name.replace('data-mf-', '');
          const isNamespace = key.endsWith('-namespace');
          
          if (isNamespace) {
            const baseKey = key.replace('-namespace', '');
            if (!metafieldsObj[baseKey]) metafieldsObj[baseKey] = {};
            metafieldsObj[baseKey].namespace = attr.value;
          } else {
            if (!metafieldsObj[key]) metafieldsObj[key] = {};
            metafieldsObj[key][key] = attr.value;
          }
        });
        
        // Convert to array format expected by dialog
        Object.entries(metafieldsObj).forEach(([key, value]) => {
          metafieldsArray.push({
            ...value,
            key: key,
            namespace: value.namespace || 'cm_digital'
          });
        });
        
        //console.log('Extracted metafields:', metafieldsArray);
        variantData.metafields = metafieldsArray;

        // TEST: Requirements modal styled exactly like the real requirements dialog
        // Create dialog if it doesn't exist
        let testDialog = document.getElementById('test-requirements-modal');
        if (!testDialog) {
          testDialog = document.createElement('dialog');
          testDialog.id = 'test-requirements-modal';
          testDialog.className = 'requirements-dialog requirements-dialog-element';
          
          // Build structure exactly like requirements-dialog.liquid
          const dialogContent = document.createElement('div');
          dialogContent.className = 'dialog-content';
          
          // Header
          const dialogHeader = document.createElement('div');
          dialogHeader.className = 'dialog-header';
          
          const dialogTitle = document.createElement('h2');
          dialogTitle.className = 'dialog-title';
          dialogTitle.textContent = 'Digital Right Management';
          
          const closeBtn = document.createElement('button');
          closeBtn.type = 'button';
          closeBtn.className = 'dialog-close-btn';
          closeBtn.setAttribute('aria-label', 'Close');
          closeBtn.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          `;
          closeBtn.onclick = () => testDialog.close();
          
          dialogHeader.appendChild(dialogTitle);
          dialogHeader.appendChild(closeBtn);
          
          // Body
          const dialogBody = document.createElement('div');
          dialogBody.className = 'dialog-body';
          
          const titleModal = document.createElement('h5');
          titleModal.className = 'dialog-title-modal';
          titleModal.id = 'test-dialog-title-modal';
          
          const cmrpTable = document.createElement('div');
          cmrpTable.className = 'cmrp-drm-table theme-2';
          cmrpTable.id = 'test-cmrp-drm-table';
          
          dialogBody.appendChild(titleModal);
          dialogBody.appendChild(cmrpTable);
          
          // Footer
          const dialogFooter = document.createElement('div');
          dialogFooter.className = 'dialog-footer';
          
          const closeButton = document.createElement('button');
          closeButton.type = 'button';
          closeButton.className = 'dialog-close-button';
          closeButton.textContent = 'CLOSE';
          closeButton.onclick = () => testDialog.close();
          
          dialogFooter.appendChild(closeButton);
          
          // Assemble
          dialogContent.appendChild(dialogHeader);
          dialogContent.appendChild(dialogBody);
          dialogContent.appendChild(dialogFooter);
          testDialog.appendChild(dialogContent);
          
          document.body.appendChild(testDialog);
        }
        
        // Build table exactly like openRequirementsDialog does
        const table = document.createElement('table');
        table.className = 'crmTable';
        const tbody = document.createElement('tbody');
        let publisher_name = '';
        let ebook_format = '';
        
        if (variantData.metafields && Array.isArray(variantData.metafields) && variantData.metafields.length > 0) {
          variantData.metafields.forEach(field => {
            const value = field;
            if (value) {
              if(value.hasOwnProperty("publisher_name") && value["namespace"]=="cm_digital"){
                const tr = document.createElement('tr');
                tr.className = "crmTableTr";
                const tdLabel = document.createElement('td');
                tdLabel.className = 'bold-text crmTableTd';
                tdLabel.textContent = "Publisher";
                const tdValue = document.createElement('td');
                tdValue.className = "crmTableTd";
                tdValue.textContent = value["publisher_name"];
                publisher_name = value["publisher_name"];
                tr.appendChild(tdLabel);
                tr.appendChild(tdValue);
                tbody.appendChild(tr);
              }
              if(value.hasOwnProperty("ebook_format") && value["namespace"]=="cm_digital"){
                const tr = document.createElement('tr');
                tr.className = "crmTableTr";
                const tdLabel = document.createElement('td');
                tdLabel.className = 'bold-text crmTableTd';
                tdLabel.textContent = "Format";
                const tdValue = document.createElement('td');
                tdValue.className = "crmTableTd";
                tdValue.textContent = value["ebook_format"];
                ebook_format = value["ebook_format"];
                tr.appendChild(tdLabel);
                tr.appendChild(tdValue);
                tbody.appendChild(tr);
              }
              if(value.hasOwnProperty("download_provider_name") && value["namespace"]=="cm_digital"){
                const tr = document.createElement('tr');
                tr.className = "crmTableTr";
                const tdLabel = document.createElement('td');
                tdLabel.className = 'bold-text crmTableTd';
                tdLabel.textContent = "Download Provider";
                const tdValue = document.createElement('td');
                tdValue.className = "crmTableTd";
                tdValue.textContent = value["download_provider_name"];
                tr.appendChild(tdLabel);
                tr.appendChild(tdValue);
                tbody.appendChild(tr);
              }
              if(value.hasOwnProperty("isbn") && value["namespace"]=="cm_digital"){
                const tr = document.createElement('tr');
                tr.className = "crmTableTr";
                const tdLabel = document.createElement('td');
                tdLabel.className = 'bold-text crmTableTd';
                tdLabel.textContent = "Digital ISBN";
                const tdValue = document.createElement('td');
                tdValue.className = "crmTableTd";
                tdValue.textContent = value["isbn"];
                tr.appendChild(tdLabel);
                tr.appendChild(tdValue);
                tbody.appendChild(tr);
              }
              if(value.hasOwnProperty("refundable") && value["namespace"]=="cm_digital"){
                const tr = document.createElement('tr');
                tr.className = "crmTableTr";
                const tdLabel = document.createElement('td');
                tdLabel.className = 'bold-text crmTableTd';
                tdLabel.textContent = "Refundable";
                const tdValue = document.createElement('td');
                tdValue.className = "crmTableTd";
                tdValue.textContent = (value["refundable"] === 'true') ? "Yes" : "No";
                tr.appendChild(tdLabel);
                tr.appendChild(tdValue);
                tbody.appendChild(tr);
              }
              if(value.hasOwnProperty("partial_content") && value["namespace"]=="cm_digital"){
                const tr = document.createElement('tr');
                tr.className = "crmTableTr";
                const tdLabel = document.createElement('td');
                tdLabel.className = 'bold-text crmTableTd';
                tdLabel.textContent = "Partial Content";
                const tdValue = document.createElement('td');
                tdValue.className = "crmTableTd";
                tdValue.textContent = (value["partial_content"] === 'true') ? "Yes" : "No";
                tr.appendChild(tdLabel);
                tr.appendChild(tdValue);
                tbody.appendChild(tr);
              }
              if(value.hasOwnProperty("subscription") && value["namespace"]=="cm_digital"){
                if(value["subscription"].trim()!= "0" && value["subscription"].trim() != "999"){
                  const tr = document.createElement('tr');
                  tr.className = "crmTableTr";
                  const tdLabel = document.createElement('td');
                  tdLabel.className = 'bold-text crmTableTd';
                  tdLabel.textContent = "Available to Rent?";
                  const tdValue = document.createElement('td');
                  tdValue.className = "crmTableTd";
                  tdValue.textContent = value["subscription"];
                  tr.appendChild(tdLabel);
                  tr.appendChild(tdValue);
                  tbody.appendChild(tr);
                }
              }
              if(value.hasOwnProperty("print_limit") && value["namespace"]=="cm_digital"){
                const tr = document.createElement('tr');
                tr.className = "crmTableTr";
                const tdLabel = document.createElement('td');
                tdLabel.className = 'bold-text crmTableTd';
                tdLabel.textContent = "Print Limit";
                const tdValue = document.createElement('td');
                tdValue.className = "crmTableTd";
                tdValue.textContent = value["print_limit"];
                tr.appendChild(tdLabel);
                tr.appendChild(tdValue);
                tbody.appendChild(tr);
              }
              if(value.hasOwnProperty("copy_paste_limit") && value["namespace"]=="cm_digital"){
                const tr = document.createElement('tr');
                tr.className = "crmTableTr";
                const tdLabel = document.createElement('td');
                tdLabel.className = 'bold-text crmTableTd';
                tdLabel.textContent = "Copy/Paste Limit";
                const tdValue = document.createElement('td');
                tdValue.className = "crmTableTd";
                tdValue.textContent = value["copy_paste_limit"];
                tr.appendChild(tdLabel);
                tr.appendChild(tdValue);
                tbody.appendChild(tr);
              }
              if(value.hasOwnProperty("device_sharing_limit") && value["namespace"]=="cm_digital"){
                const tr = document.createElement('tr');
                tr.className = "crmTableTr";
                const tdLabel = document.createElement('td');
                tdLabel.className = 'bold-text crmTableTd';
                tdLabel.textContent = "Device Sharing Limit";
                const tdValue = document.createElement('td');
                tdValue.className = "crmTableTd";
                tdValue.textContent = value["device_sharing_limit"];
                tr.appendChild(tdLabel);
                tr.appendChild(tdValue);
                tbody.appendChild(tr);
              }
              if(value.hasOwnProperty("access") && value["namespace"]=="cm_digital"){
                const tr = document.createElement('tr');
                tr.className = "crmTableTr";
                const tdLabel = document.createElement('td');
                tdLabel.className = 'bold-text crmTableTd';
                tdLabel.textContent = "Access";
                const tdValue = document.createElement('td');
                tdValue.className = "crmTableTd";
                tdValue.textContent = value["access"];
                tr.appendChild(tdLabel);
                tr.appendChild(tdValue);
                tbody.appendChild(tr);
              }
            }
          });
        }
        
        table.appendChild(tbody);
        
        // Update modal content
        const cmrpTable = document.getElementById('test-cmrp-drm-table');
        if (cmrpTable) {
          cmrpTable.innerHTML = '';
          cmrpTable.appendChild(table);
        }
        
        const titleModal = document.getElementById('test-dialog-title-modal');
        if (titleModal) {
          if (publisher_name && ebook_format) {
            titleModal.textContent = `${productTitle} - ${publisher_name} on ${ebook_format}`;
          } else {
            titleModal.textContent = productTitle;
          }
        }
        
        // Show modal
        testDialog.showModal();
      });
    });
  }
  async shouldApplyFeeForPublisherDialog(publisherCode) {
    const api_url = window.digital_product_config?.api_url ?? ''
    const store_number = window.digital_product_config?.store_number ?? ''

    if(api_url && store_number) {
      try {
        const apiResponse = await fetch(`${api_url}?storeNumber=${store_number}&downloadprovidercode=${publisherCode}`)
        const result = await apiResponse.json();
        if(result && result.length > 0 && result[0].isApplied) {
          return true;
        } else {
          return false
        }
      } catch (error) {
        console.error(error);
      }
      
    } else {
      // Digital fee config not found
    }
  }
  async fetchMetafieldsDialog(variantId) {
    if (typeof window !== 'undefined' && window.fetchMetafieldsOnce) {
      return window.fetchMetafieldsOnce(variantId);
    }

    const proxyPath = '/apps/shopifyData/getMetafields';
    const url = `${proxyPath}?query=gid://shopify/ProductVariant/${variantId}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to fetch Metafields');
      }
    } catch (error) {
      console.error('Error fetching Metafields:', error);
      throw error;
    }
  }
  async addFeeForPublisherDialog(quantity, feePublisherCode) {
    try {
      
      const feeProductHandle = window.digital_product_config?.product_handle ?? '';
      
      // Get the fee product to find its variant ID
      const productResponse = await fetch(`/products/${feeProductHandle}.js`);
      if (!productResponse.ok) {
        console.error('Digital delivery fee product not found');
        return;
      }
      
      const feeProduct = await productResponse.json();
      const feeVariantId = feeProduct.variants[0]?.id;
      
      if (!feeVariantId) {
        console.error('No variant found for digital delivery fee product');
        return;
      }
      const getMetafieldValue = (key) => {
        const metafield = metafields.find(m => m.key === key);
        return metafield ? metafield.value : ''; 
      };
      const metafields = await this.fetchMetafieldsDialog(feeVariantId);
      // Add the fee product to cart
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: feeVariantId,
          quantity: quantity,
          properties: {
            '_digital_delivery_fee': 'true',
            '_is_digital': 'true',
            '_fee_type': 'online_store',
            '_department': getMetafieldValue('department'), 
	          '_sub_department': getMetafieldValue('sub_department'), 
	          '_class': getMetafieldValue('class'), 
	          '_sub_class': getMetafieldValue('sub_class')
          }
        })
      });
      
    } catch (error) {
      console.error('Failed to add digital delivery fee:', error);
    }
  }
  // Handle Add to Bag button for multiple selections
  setupAddToBagMultipleDialog() {
    // Find the Add to Bag button - check both in quick-add modal and on product page
    const quickAddModal = document.querySelector('.quick-add-modal');
    let addToBagButton;
    
    if (quickAddModal) {
      // In quick-add modal, look for button within the modal
      addToBagButton = quickAddModal.querySelector('.add-to-cart-button, button[type="submit"][name="add"]');
    }
    
    // Fallback to original selector for product page
    if (!addToBagButton) {
      addToBagButton = document.querySelector('.textbook-variant-picker ~ * .add-to-cart-button, .textbook-variant-picker ~ * button[type="submit"][name="add"]');
    }
    
    if (!addToBagButton) return;

    // Store original onclick handler
    const originalHandler = addToBagButton.onclick;

    // Override the button click - use capture phase to intercept before product-form-component
    addToBagButton.addEventListener('click', async (e) => {
      // Check if any checkboxes are selected - scope to quick-add modal if we're in one
      const scope = quickAddModal || document;
      const checkedBoxes = scope.querySelectorAll('.purchase-checkbox:checked');
      if (checkedBoxes.length === 0) {
        // No checkboxes selected, dont add to the cart 
        const selectionSpan = document.querySelector('.makeSelection');
        selectionSpan.hidden = false;       
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return;        
      }else{
        const selectionSpan = document.querySelector('.makeSelection');
        selectionSpan.hidden = true;       
      }
      // Prevent default form submission and stop all other handlers
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Also prevent form submission if button is inside a form
      const form = addToBagButton.closest('form');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }, { once: true, capture: true });
      }
      
      // Set flag to prevent variant rendering from running during add-to-cart
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) {
        modalContent.setAttribute('data-adding-to-cart', 'true');
      }
      
      this.disableAddToBagButtonDialog();
      // Add all selected variants to cart
      const items = Array.from(checkedBoxes).map(checkbox => ({
        productId: checkbox.dataset.productId,
        variantId: checkbox.dataset.variantId,
        format: checkbox.dataset.format,
        type: checkbox.dataset.type,
        price: checkbox.dataset.price,
        variantTitle: checkbox.dataset.variantTitle,
        isRental: checkbox.dataset.isRental,
        bookRentDueDate: checkbox.dataset.bookRentDueDate,
        isDigital:  checkbox.dataset.isDigital,
        publisherCode: checkbox.dataset.publisherCode,
        nonReturnProcessingFee: checkbox.dataset.nonReturnProcessingFee,
        nonReturnCharge: checkbox.dataset.nonReturnCharge,
        department: checkbox.dataset.department,
        subDepartment: checkbox.dataset.subDepartment,
        class: checkbox.dataset.class,
        subClass: checkbox.dataset.subClass,
        rentalOnlySku: checkbox.dataset.rentalOnlySku,
        bookIsbn: checkbox.dataset.bookIsbn,
        bookEdition: checkbox.dataset.bookEdition,
        subscriptionLength: checkbox.dataset.subscriptionLength,
      }));
      const quantitySelect = document.querySelector('select[name="quantity"]');
      const selectedValue = quantitySelect.value;

      const itemNeedsDdcsRentalRefund = (it) => {
        const isBuyType = /buy/i.test(String(it.type || ''));
        return (
          !isBuyType &&
          (it.isRental === 'true' || /rent/i.test(String(it.type || '')))
        );
      };
      if (items.some(itemNeedsDdcsRentalRefund)) {
        try {
          await (window.__pdpDdcsRentalReadyPromise ?? Promise.resolve());
        } catch {
          /* ignore */
        }
      }

      try {
        // Add each selected variant to cart
        for (const item of items) {
          const properties = {
            'Format': item.format,
            'Type': item.type,
          };
          if (item.bookEdition && item.bookEdition !== '') {              
            properties['Edition'] = item.bookEdition;
          }
          if (item.bookIsbn && item.bookIsbn !== '') {              
            properties['ISBN'] = item.bookIsbn;
          }

          const isBuyType = /buy/i.test(String(item.type || ''));
          if (item.isRental === 'true') {
            properties['_rental_flag'] = true;
            properties['_non_return_processing_fee'] = item.nonReturnProcessingFee;
            properties['_non_return_charge'] = item.nonReturnCharge;
            if (!isBuyType && item.bookRentDueDate && item.bookRentDueDate !== '') {
              const dateParts = item.bookRentDueDate.split('-');
              const year = dateParts[0].slice(-2);
              const formattedDate = `${dateParts[1]}/${dateParts[2]}/${year}`;
              properties['_rental_expiration_date'] = item.bookRentDueDate;
              properties['Rental Due'] = formattedDate;
            }
          }
          if (item.isDigital === 'true') {
            properties['_is_digital'] = item.isDigital;
            if(await this.shouldApplyFeeForPublisherDialog(item.publisherCode)){
              properties["_requires_digital_delivery_fee"] = "yes"
              await this.addFeeForPublisherDialog(selectedValue, item.publisherCode)
            }else{
              properties["_requires_digital_delivery_fee"] = "no"
            }
            if (!isBuyType && item.subscriptionLength && item.subscriptionLength !== '' && item.subscriptionLength !== '999' && item.subscriptionLength !== '0' ) {              
              properties['Rental Duration'] = item.subscriptionLength + " days";
            }
          } 

          if (item.department) {
            properties['_department'] = item.department;
          }
          if (item.subDepartment) {
            properties['_sub_department'] = item.subDepartment;
          }
          if (item.class) {
            properties['_class'] = item.class;
          }
          if (item.subClass) {
            properties['_sub_class'] = item.subClass;
          }

          if (item.rentalOnlySku === 'true') {
            properties['_CM_Rental_Only_SKU'] = item.rentalOnlySku;
          }

          if (itemNeedsDdcsRentalRefund(item)) {
            const getPdp = window.getPdpRentalCampusTermLineProperties;
            if (typeof getPdp === 'function') {
              const ddcs = getPdp();
              properties['_rental_refund_grace_period'] = String(ddcs._rental_refund_grace_period ?? '');
              properties['_rental_refund_date'] = String(ddcs._rental_refund_date ?? '');
            }
          }

          this.enableAddToBagButtonDialog();
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: item.variantId,
              quantity: selectedValue,
              properties: properties
            }),
          });

          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.message || `Failed to add ${item.variantTitle} to cart`);
          }
        }

        // Refresh cart data
        const cartData = await fetch('/cart.js').then(r => r.json());
        
        // Show success animation on button briefly
        addToBagButton.classList.add('atc-added');
        
        // Close the modal immediately to prevent variant duplication from being visible
        // Remove the adding-to-cart flag before closing
        if (modalContent) {
          modalContent.removeAttribute('data-adding-to-cart');
        }
        
        // Close modal immediately
        this.closeDialog();
        
        // Dispatch cart update event after closing to avoid triggering variant rendering
        setTimeout(() => {
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cartData,
                sourceId: 'textbook-variant-picker',
                data: {
                  itemCount: cartData.item_count,
                  source: 'textbook-variant-picker',
                },
              },
            })
          );
          addToBagButton.classList.remove('atc-added');
          this.closeDialog();
        }, 3000);

      } catch (error) {
        console.error('Error adding items to cart:', error);
        alert('Error adding items to cart: ' + error.message);
        
        // Remove the adding-to-cart flag on error
        const modalContent = document.getElementById('quick-add-modal-content');
        if (modalContent) {
          modalContent.removeAttribute('data-adding-to-cart');
        }
      }
    }, true); // Use capture phase to intercept before product-form-component
  }
  async fetchPrintDigitalProductsDialog(queryData) {
    const proxyPath = '/apps/shopifyData/getDigitalProducts';
    const url = `${proxyPath}?query=${queryData}`;

    try {
        const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'same-origin'
        });

        if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          const raw = result.products || [];
          const products = raw.filter((p) => String(p && p.status || '').toUpperCase() !== 'DRAFT');
          products.forEach((p) => {
            (p.variants || []).forEach((v) => {
              if (v != null && typeof v.price === 'number') v.price = v.price / 100;
            });
          });
          return products;
        } else {
        throw new Error(result.error || 'Failed to fetch products');
        }

    } catch (error) {
        throw error;
    }
  }
  getMetafieldValueDialog(obj,nameSpace, field) {   
    for (const metafield of obj) {
        if (metafield.namespace === nameSpace && metafield[field]) {
            return metafield[field];
        }
    }
    return null; 
  }
  variantTypeSortIndex(option2, order) {
    const lower = (option2 || '').toLowerCase().trim();
    for (let i = 0; i < order.length; i++) {
      if (lower.indexOf(order[i]) !== -1) return i;
    }
    return 999;
  }
  variantPrice(v) {
    const p = v && (v.price != null ? v.price : (v.priceV2 && v.priceV2.amount));
    const n = parseFloat(p);
    return isNaN(n) ? 0 : n;
  }
  sortDigitalVariants(a, b) {
    const order = ['buy', 'rent'];
    const typeDiff = this.variantTypeSortIndex(a.option2, order) - this.variantTypeSortIndex(b.option2, order);
    if (typeDiff !== 0) return typeDiff;
    return this.variantPrice(a) - this.variantPrice(b);
  }
  sortPrintVariants(a, b) {
    const order = ['buy new', 'buy used', 'rent new', 'rent used'];
    const typeDiff = this.variantTypeSortIndex(a.option2, order) - this.variantTypeSortIndex(b.option2, order);
    if (typeDiff !== 0) return typeDiff;
    return this.variantPrice(a) - this.variantPrice(b);
  }
  applyPrintDisplayRulesDialog(product, format) {
    if (!product.variants) return false;

    const formatLower = format.toLowerCase();

    // @ts-ignore
    let formatVariants = product.variants.filter(variant => {
      const option1 = variant.option1?.toLowerCase() || '';
      return option1.includes(formatLower);
    });

    if (formatVariants.length === 0) return false;

    /*
      FMVP-1051
      For SKUs with cm_rental.rental_only_sku = "true"
      then do not consider "Buy Used" or "Buy New" variants
      for the "From" price, mirroring the purchase options UI.
    */
    const hasRentalOnlySku =
      // @ts-ignore
      formatVariants.some(variant =>
        Array.isArray(variant.metafields) &&
        variant.metafields.some(metafield =>
          metafield.namespace === 'cm_rental' &&
          (metafield.rental_only_sku === 'true' || metafield.rental_only_sku === true)
        )
      );

    if (hasRentalOnlySku) {
      // @ts-ignore
      formatVariants = formatVariants.filter(
        variant => variant.option2 !== 'Buy New' && variant.option2 !== 'Buy Used'
      );
    }

    if (formatVariants.length === 0) return 'From $0.00';

    // Align "From" price with options that can actually be rendered for each format,
    // mirroring the rules used when rendering purchase options in html-renderer.js.
    let displayableVariants;

    if (formatLower === 'print') {
      // FMVP-1051: product-level flag to allow only "New" options
      const newOnly =
        product.metafields?.new_book_only_on_efollett_flag === true ||
        product.metafields?.new_book_only_on_efollett_flag === 'true';

      const hasBuyNewAvailable = product.variants.some(
        v =>
          v.option1 === 'Print' &&
          v.option2 === 'Buy New' &&
          v.available === true
      );
      const hasBuyUsedAvailable = product.variants.some(
        v =>
          v.option1 === 'Print' &&
          v.option2 === 'Buy Used' &&
          v.available === true
      );
      const hasRentNewAvailable = product.variants.some(
        v =>
          v.option1 === 'Print' &&
          v.option2 === 'Rent New' &&
          v.available === true
      );
      const hasRentUsedAvailable = product.variants.some(
        v =>
          v.option1 === 'Print' &&
          v.option2 === 'Rent Used' &&
          v.available === true
      );

      /** @type {Record<string, any[]>} */
      const variantsByType = {};
      formatVariants.forEach(variant => {
        const type = variant.option2 || 'Buy';
        if (!variantsByType[type]) variantsByType[type] = [];
        variantsByType[type].push(variant);
      });

      /** @type {any[][]} */
      const variantGroupsToUse = [];

      Object.keys(variantsByType).forEach(type => {
        const variants = variantsByType[type]
          .slice()
          .sort((a, b) => parseFloat(a.price) - parseFloat(b.price));

        const base = variants[0];
        if (!base) return;

        if (newOnly) {
          if (base.option2 && base.option2.includes('Used')) {
            return;
          } else {
            variantGroupsToUse.push(variants);
            return;
          }
        }

      if (!base.available && base.option2 === 'Buy New') {
          if (hasBuyUsedAvailable) {
            return;
          } else {
            variantGroupsToUse.push(variants);
            return;
          }
        } else if (!base.available && base.option2 === 'Buy Used') {
          if (hasBuyNewAvailable) {
            return;
          }
        } else if (base.option2 === 'Buy New') {
          const buyUsedVariant = product.variants.find(
            v =>
              v.option2 === 'Buy Used' &&
              v.available === true &&
              v.option1 === 'Print'
          );
          const buyUsedPrice =
            buyUsedVariant && !isNaN(parseFloat(buyUsedVariant.price))
              ? parseFloat(buyUsedVariant.price)
              : undefined;
          const buyNewPrice = !isNaN(parseFloat(base.price))
            ? parseFloat(base.price)
            : undefined;

          if (
            buyUsedPrice !== undefined &&
            buyNewPrice !== undefined &&
            buyUsedPrice >= buyNewPrice
          ) {
            return;
          } else {
            variantGroupsToUse.push(variants);
            return;
          }
        } else if (!base.available && base.option2 === 'Rent Used') {
          if (!hasRentNewAvailable) {
            return;
          } else {
            variantGroupsToUse.push(variants);
            return;
          }
        } else if (!base.available && base.option2 === 'Rent New') {
          const rentUsedVariant = product.variants.find(
            v => v.option2 === 'Rent Used' && v.option1 === 'Print'
          );
          const rentUsedPrice =
            rentUsedVariant && !isNaN(parseFloat(rentUsedVariant.price))
              ? parseFloat(rentUsedVariant.price)
              : undefined;
          const rentNewPrice = !isNaN(parseFloat(base.price))
            ? parseFloat(base.price)
            : undefined;

          if (!hasRentUsedAvailable) {
            return;
          } else {
            if (
              rentUsedPrice !== undefined &&
              rentNewPrice !== undefined &&
              rentUsedPrice >= rentNewPrice
            ) {
              return;
            } else {
              variantGroupsToUse.push(variants);
              return;
            }
          }
        } else if (base.option2 === 'Rent New') {
          const rentUsedVariant = product.variants.find(
            v => v.option2 === 'Rent Used' && v.option1 === 'Print'
          );
          const rentUsedPrice =
            rentUsedVariant && !isNaN(parseFloat(rentUsedVariant.price))
              ? parseFloat(rentUsedVariant.price)
              : undefined;
          const rentNewPrice = !isNaN(parseFloat(base.price))
            ? parseFloat(base.price)
            : undefined;

          if (
            rentUsedPrice !== undefined &&
            rentNewPrice !== undefined &&
            rentUsedPrice >= rentNewPrice
          ) {
            return;
          } else {
            variantGroupsToUse.push(variants);
            return;
          }
        } else {
          variantGroupsToUse.push(variants);
          return;
        }
      });

      
      displayableVariants = variantGroupsToUse.flat();
    } else {
      displayableVariants = formatVariants;
    }
    
    return displayableVariants;
  }
  renderPrintProductsDialog(printProducts){
    
    const button = document.createElement('button');
    button.setAttribute('class', 'format-tab active');
    button.setAttribute('data-format', 'print');
    button.setAttribute('onclick', 'toggleFormatCollapsePDP(this)');
    button.setAttribute('data-testid', 'format-tab-print');
    let span = ` <span class="format-tab-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M2.66824 12.5741C2.66741 12.6048 2.66699 12.6356 2.66699 12.6663C2.66699 12.7363 2.67778 12.8038 2.69779 12.8672C2.77098 13.3008 2.97277 13.7062 3.2828 14.0251C3.67977 14.4334 4.22225 14.6663 4.79199 14.6663H12.667C13.0352 14.6663 13.3337 14.3679 13.3337 13.9997V1.99967C13.3337 1.63148 13.0352 1.33301 12.667 1.33301H4.79199C4.22225 1.33301 3.67977 1.56598 3.2828 1.9743C2.88644 2.38197 2.66699 2.931 2.66699 3.49967V12.4997C2.66699 12.5245 2.66741 12.5493 2.66824 12.5741ZM4.23879 2.90373C4.3888 2.74944 4.58819 2.66634 4.79199 2.66634H12.0003V9.99967H4.79199C4.50819 9.99967 4.24154 10.068 4.00033 10.1879V3.49967C4.00033 3.2727 4.08816 3.05866 4.23879 2.90373ZM12.0003 11.333V13.333H4.79199C4.58819 13.333 4.3888 13.2499 4.23879 13.0956C4.10275 12.9557 4.01793 12.7676 4.00277 12.5652C4.0207 12.1962 4.13553 11.8697 4.29947 11.6449C4.47585 11.403 4.6622 11.333 4.79199 11.333H12.0003Z" fill="#080909"></path>
                  </svg>
                </span>
                <span class="format-tab-label">Print</span>
                <span class="format-tab-price">from $0.00</span> `;
    button.innerHTML = span;
    const formatTabs = document.querySelector('.format-tabs');
    formatTabs.append(button);
    
    const div = document.createElement('div');
    div.setAttribute('class', 'format-tab-panel active');
    div.setAttribute('data-format', 'print');
    div.setAttribute('id', 'format-panel-print')
    div.setAttribute('style', 'display: block;');
    
    let accordionHtml = ` <div class="format-tab-panel-content"> `;
    accordionHtml += ` <div class="accordion-variant-options variant-format-option-wrapper"> `;
    let rentLabel = false;
    const allPrintItems = [];
    for (let i = 0; i < printProducts.length; i++) {
      for (let index = 0; index < printProducts[i].variants.length; index++) {
        const variantIndex = printProducts[i].variants[index];
        allPrintItems.push({ variant: variantIndex, productTitle: printProducts[i].title });
      }
    }
    allPrintItems.sort(function(a, b) { return this.sortPrintVariants(a.variant, b.variant); });
    for (let d = 0; d < allPrintItems.length; d++) {
      const variantPrint = allPrintItems[d].variant;
      const productTitle = allPrintItems[d].productTitle;
      const vid = variantPrint.id.split('/').pop();
      const availabilityClass = variantPrint.available ? '' : ' unavailable';
      const isRentalRaw = this.getMetafieldValueDialog(variantPrint.metafields, "cm_rental", "is_rental");
      const isRental = isRentalRaw === true || isRentalRaw === 'true';
      const smartPickClass = isRental ? ' smart-pick' : '';
      const displayLabel = (variantPrint.option2 || '').toUpperCase();
      const opt2Lower = (variantPrint.option2 || '').toLowerCase();
      if (opt2Lower.includes('rent')) {
        rentLabel = true;
      }
      const showUsedTooltip = opt2Lower.includes('buy used') || opt2Lower.includes('rent used');
      accordionHtml += `  <div class="accordion-variant-option purchase-type${smartPickClass}${availabilityClass}" data-format-specific="print" data-variant-id="${vid}" data-purchase-type="print"> `;
      if (variantPrint.available) {
        accordionHtml += `  <label for="variant-${vid}" class="accordion-variant-option-label"> `;
        accordionHtml += `  <div class="purchase-checkbox-container" onclick="event.stopPropagation()"> `;
        accordionHtml += `  <input `;
        accordionHtml += `  type="checkbox" `;
        accordionHtml += `  id="variant-${vid}" `;
        accordionHtml += `  name="purchase-checkbox-${vid}" `;
        accordionHtml += `  class="purchase-checkbox" `;
        accordionHtml += `  data-format="${variantPrint.option1}" `;
        accordionHtml += `  data-type="${variantPrint.option2}" `;
        accordionHtml += `  data-is-digital="false" `;
        accordionHtml += `  data-publisher-code="${this.getMetafieldValueDialog(variantPrint.metafields, "course_materials", "publisher_code")}" `;
        accordionHtml += `  data-variant-id="${vid}" `;
        accordionHtml += `  data-price="${variantPrint.price}" `;
        accordionHtml += `  data-is-rental="${isRental ? 'true' : 'false'}" `;
        accordionHtml += `  data-book-rent-due-date="" `;
        accordionHtml += `  data-non-return-processing-fee="0" `;
        accordionHtml += `  data-non-return-charge="0" `;
        accordionHtml += `  data-option-value-id="" `;
        accordionHtml += `  data-option-available="${variantPrint.available}" `;
        accordionHtml += `  data-variant-title="${productTitle} ${variantPrint.option1} / ${variantPrint.option2}" `;
        accordionHtml += `  data-department="${this.getMetafieldValueDialog(variantPrint.metafields, "taxonomy", "department")}" `;
        accordionHtml += `  data-sub-department="${this.getMetafieldValueDialog(variantPrint.metafields, "taxonomy", "sub_department")}" `;
        accordionHtml += `  data-class="${this.getMetafieldValueDialog(variantPrint.metafields, "taxonomy", "class")}" `;
        accordionHtml += `  data-sub-class="${this.getMetafieldValueDialog(variantPrint.metafields, "taxonomy", "sub_class")}" `;
        accordionHtml += `  data-rental-only-sku="${this.getMetafieldValueDialog(variantPrint.metafields, "cm_rental", "rental_only_sku")}" `;
        accordionHtml += `  data-book-isbn="${this.getMetafieldValueDialog(variantPrint.metafields, "course_materials", "isbn_ean")}" `;
        accordionHtml += `  data-book-edition="${this.getMetafieldValueDialog(variantPrint.metafields, "course_materials", "edition")}" `;
        accordionHtml += `  data-subscription-length="${(this.getMetafieldValueDialog(variantPrint.metafields, "cm_digital", "subscription") || '')}" `;
        accordionHtml += `  > `;
        accordionHtml += `  <label for="variant-${vid}" class="checkbox-label"></label> `;
        accordionHtml += `  </div> `;
      }
      accordionHtml += `  <span class="accordion-variant-label">${displayLabel}</span> `;
      if (showUsedTooltip) {
        const textPrint = document.getElementById('used_tooltip_message')?.value || 'Used Materials. Might not include supplemental materials like access cards or CDs.';
        accordionHtml += `  <anchored-popover-component data-close-on-resize="true" data-close-on-scroll="true" class="notice-tooltip" data-variant-id="${vid}"> `;
        accordionHtml += `  <button ref="trigger" popovertarget="variant-popover-container-${vid}" class="notice-tooltip__icon" popovertargetaction="toggle" onclick="event.stopPropagation()"> `;
        accordionHtml += `  <svg class="question-icon" width="16" height="16" viewBox="0 0 16 16" fill="none"> `;
        accordionHtml += `  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/> `;
        accordionHtml += `  <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/> `;
        accordionHtml += `  </svg> `;
        accordionHtml += `  </button> `;
        accordionHtml += `  <div ref="popover" id="variant-popover-container-${vid}" class="notice-tooltip__container" popover="auto"> `;
        accordionHtml += `  <p>${textPrint}</p> `;
        accordionHtml += `  </div> `;
        accordionHtml += `  </anchored-popover-component> `;
      }
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(parseFloat(variantPrint.price));
      accordionHtml += `  <div> `;
      accordionHtml += `  <span> `;
      accordionHtml += `  <span class="accordion-variant-price">${formattedPrice}</span> `;
      accordionHtml += `  </span> `;
      accordionHtml += `  </div> `;
      if (isRental) {
        accordionHtml += `  <div class="accordion-variant-rental-due"> `;
        accordionHtml += `  <span class="accordion-variant-rental-due-text">${printRentalDueLabel}</span> `;
        accordionHtml += `  <span class="accordion-variant-rental-due-date" data-refresh-date></span> `;
        accordionHtml += `  </div> `;
      }
      if (variantPrint.available && isRental) {
        accordionHtml += `  <span class="smart-pick-badge">Smart Pick</span> `;
      }
      if (variantPrint.available) {
        accordionHtml += `  </label> `;
      }
      if (variantPrint.available === false) {
        accordionHtml += `  <div class="purchase-price out-of-stock-overlay"> Out of Stock </div> `;
      }
      accordionHtml += `  </div> `;
    }
    accordionHtml += ` </div> `;
    accordionHtml += ` </div> `;
    

   
    //console.log('accordionHtml ==> ', accordionHtml);
    div.innerHTML = accordionHtml;
    const tabs = document.querySelector('.format-tabs');
    tabs.after(div);

    if(rentLabel){
      const divRent = document.createElement('div');
      divRent.setAttribute('class', 'rental-banner');
      let rentLabelHTML = ` <span class="dollar-icon">$</span>
                            Rent for the semester and save money! `;
      divRent.innerHTML = rentLabelHTML;

      const contentPrint = document.querySelector('.content-print');
      if (contentPrint) contentPrint.prepend(divRent);

    }

    const formatCollapse = document.querySelectorAll('.format-tab-panel');

    formatCollapse.forEach(element => {
      if (element.getAttribute('data-format') === 'print') {
        element.classList.remove('active');
        element.style.display = 'none';
      }
    });

    const formatTab = document.querySelectorAll('.format-tab');
    formatTab.forEach(element => {
      if (element.getAttribute('data-format') === 'print') {
        element.classList.remove('active');
      }
    });

    this.updateRentalDatesDialog();
  
  }  
  renderDigitalProductsDialog(digitalProducts){
    const button = document.createElement('button');
    button.setAttribute('class', 'format-tab active');
    button.setAttribute('data-format', 'digital');
    button.setAttribute('onclick', 'toggleFormatCollapsePDP(this)');
    button.setAttribute('data-testid', 'format-tab-digital');
    let span = ` <span class="format-tab-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M2.66824 12.5741C2.66741 12.6048 2.66699 12.6356 2.66699 12.6663C2.66699 12.7363 2.67778 12.8038 2.69779 12.8672C2.77098 13.3008 2.97277 13.7062 3.2828 14.0251C3.67977 14.4334 4.22225 14.6663 4.79199 14.6663H12.667C13.0352 14.6663 13.3337 14.3679 13.3337 13.9997V1.99967C13.3337 1.63148 13.0352 1.33301 12.667 1.33301H4.79199C4.22225 1.33301 3.67977 1.56598 3.2828 1.9743C2.88644 2.38197 2.66699 2.931 2.66699 3.49967V12.4997C2.66699 12.5245 2.66741 12.5493 2.66824 12.5741ZM4.23879 2.90373C4.3888 2.74944 4.58819 2.66634 4.79199 2.66634H12.0003V9.99967H4.79199C4.50819 9.99967 4.24154 10.068 4.00033 10.1879V3.49967C4.00033 3.2727 4.08816 3.05866 4.23879 2.90373ZM12.0003 11.333V13.333H4.79199C4.58819 13.333 4.3888 13.2499 4.23879 13.0956C4.10275 12.9557 4.01793 12.7676 4.00277 12.5652C4.0207 12.1962 4.13553 11.8697 4.29947 11.6449C4.47585 11.403 4.6622 11.333 4.79199 11.333H12.0003Z" fill="#080909"></path>
                  </svg>
                </span>
                <span class="format-tab-label">Digital</span>
                <span class="format-tab-price">from $0.00</span> `;
    button.innerHTML = span;
    const formatTabs = document.querySelector('.format-tabs');
    formatTabs.prepend(button);

    const div = document.createElement('div');
    div.setAttribute('class', 'format-tab-panel active');
    div.setAttribute('data-format', 'digital');
    div.setAttribute('id', 'format-panel-digital')
    div.setAttribute('style', 'display: block;');
    
    let accordionHtml = ` <div class="format-tab-panel-content"> `;
    accordionHtml += ` <div class="accordion-variant-options variant-format-option-wrapper"> `;
    let rentLabel = false;
    const digitalProd = JSON.parse(document.getElementById("digitalProd").value);
    const allDigitalItems = [];
    for (let i = 0; i < digitalProducts.length; i++) {
      const digitalProduct = digitalProducts[i].variants || [];
      const productTitle = digitalProducts[i].title;
      for (let v = 0; v < digitalProduct.length; v++) {
        allDigitalItems.push({ variant: digitalProduct[v], productTitle: productTitle });
      }
    }
    allDigitalItems.sort(function(a, b) { return this.sortDigitalVariants(a.variant, b.variant); });
    for (let d = 0; d < allDigitalItems.length; d++) {
      const variantDigital = allDigitalItems[d].variant;
      const productTitle = allDigitalItems[d].productTitle;
      const vid = variantDigital.id.split('/').pop();
      const availabilityClass = variantDigital.available ? '' : ' unavailable';
      const subscriptionStr = this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "subscription");
      let subscription = 0;
      if (subscriptionStr !== '' && subscriptionStr != null) {
        subscription = parseInt(subscriptionStr, 10) || 0;
      }
      const typeLower = (variantDigital.option2 || '').toLowerCase();
      let displayLabel;
      if (typeLower.includes('rent') && subscriptionStr) {
        displayLabel = subscription + ' days rental';
      } else {
        displayLabel = (variantDigital.option2 || '').toUpperCase();
      }
      if (variantDigital.option2.toLowerCase().includes('rent')) {
        rentLabel = true;
      }
      accordionHtml += `  <div class="accordion-variant-option purchase-type${availabilityClass}" data-format-specific="digital" data-variant-id="${vid}" data-purchase-type="digital"> `;
      if (variantDigital.available) {
        accordionHtml += `  <label for="variant-${vid}" class="accordion-variant-option-label"> `;
        accordionHtml += `  <div class="purchase-checkbox-container" onclick="event.stopPropagation()"> `;
        accordionHtml += `  <input `;
        accordionHtml += `  type="checkbox" `;
        accordionHtml += `  id="variant-${vid}" `;
        accordionHtml += `  name="purchase-checkbox-${vid}" `;
        accordionHtml += `  class="purchase-checkbox" `;
        accordionHtml += `  data-format="${variantDigital.option1}" `;
        accordionHtml += `  data-type="${variantDigital.option2}" `;
        accordionHtml += `  data-is-digital="true" `;
        accordionHtml += `  data-publisher-code="${this.getMetafieldValueDialog(variantDigital.metafields, "course_materials", "publisher_code")}" `;
        accordionHtml += `  data-variant-id="${vid}" `;
        accordionHtml += `  data-price="${variantDigital.price}" `;
        accordionHtml += `  data-is-rental="false" `;
        accordionHtml += `  data-book-rent-due-date="" `;
        accordionHtml += `  data-non-return-processing-fee="0" `;
        accordionHtml += `  data-non-return-charge="0" `;
        accordionHtml += `  data-option-value-id="" `;
        accordionHtml += `  data-option-available="${variantDigital.available}" `;
        accordionHtml += `  data-variant-title="${productTitle} ${variantDigital.option1} / ${variantDigital.option2}" `;
        accordionHtml += `  data-department="${this.getMetafieldValueDialog(variantDigital.metafields, "taxonomy", "department")}" `;
        accordionHtml += `  data-sub-department="${this.getMetafieldValueDialog(variantDigital.metafields, "taxonomy", "sub_department")}" `;
        accordionHtml += `  data-class="${this.getMetafieldValueDialog(variantDigital.metafields, "taxonomy", "class")}" `;
        accordionHtml += `  data-sub-class="${this.getMetafieldValueDialog(variantDigital.metafields, "taxonomy", "sub_class")}" `;
        accordionHtml += `  data-rental-only-sku="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_rental", "rental_only_sku")}" `;
        accordionHtml += `  data-book-isbn="${this.getMetafieldValueDialog(variantDigital.metafields, "course_materials", "isbn_ean")}" `;
        accordionHtml += `  data-book-edition="${this.getMetafieldValueDialog(variantDigital.metafields, "course_materials", "edition")}" `;
        accordionHtml += `  data-subscription-length="${subscriptionStr || ''}" `;
        accordionHtml += `  > `;
        accordionHtml += `  <label for="variant-${vid}" class="checkbox-label"></label> `;
        accordionHtml += `  </div> `;
      }
      accordionHtml += `  <span class="accordion-variant-label">${displayLabel}</span> `;
      if (digitalProd) {
		const textDigital = document.getElementById("digital_fee_notice_text").value;
        accordionHtml += `  <anchored-popover-component data-close-on-resize="true" data-close-on-scroll="true" class="notice-tooltip" data-variant-id="${vid}"> `;
        accordionHtml += `  <button ref="trigger" popovertarget="variant-popover-container-${vid}" class="notice-tooltip__icon" popovertargetaction="toggle" onclick="event.stopPropagation()"> `;
        accordionHtml += `  <svg class="question-icon" width="16" height="16" viewBox="0 0 16 16" fill="none"> `;
        accordionHtml += `  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/> `;
        accordionHtml += `  <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/> `;
        accordionHtml += `  </svg> `;
        accordionHtml += `  </button> `;
        accordionHtml += `  <div ref="popover" id="variant-popover-container-${vid}" class="notice-tooltip__container" popover="auto"> `;
        accordionHtml += `  <p>${textDigital}</p> `;
        accordionHtml += `  </div> `;
        accordionHtml += `  </anchored-popover-component> `;
      }
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(parseFloat(variantDigital.price));
      accordionHtml += `  <div> `;
      accordionHtml += `  <span> `;
      accordionHtml += `  <span class="accordion-variant-price">${formattedPrice}</span> `;
      accordionHtml += `  </span> `;
      accordionHtml += `  </div> `;
      accordionHtml += `  <div class="" data-format-specific="digital"> `;
      accordionHtml += `  <button `;
      accordionHtml += `  type="button" `;
      accordionHtml += `  class="requirements-link" `;
      accordionHtml += `  onclick="event.stopPropagation()" `;
      accordionHtml += `  data-variant-id="${vid}" `;
      accordionHtml += `  data-product-title="${productTitle}" `;
      accordionHtml += `  data-format="${variantDigital.option1}" `;
      accordionHtml += `  data-type="${variantDigital.option2}" `;
      if (this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "is_digital") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "is_digital") != "") {
        accordionHtml += `  data-mf-is_digital="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "is_digital")}" `;
        accordionHtml += `  data-mf-is_digital-namespace="cm_digital" `;
      }
      if (this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "publisher_name") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "publisher_name") != "") {
        accordionHtml += `  data-mf-publisher_name="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "publisher_name")}" `;
        accordionHtml += `  data-mf-publisher_name-namespace="cm_digital" `;
      }
      if (this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "ebook_format") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "ebook_format") != "") {
        accordionHtml += `  data-mf-ebook_format="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "ebook_format")}" `;
        accordionHtml += `  data-mf-ebook_format-namespace="cm_digital" `;
      }
      if (this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "download_provider_name") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "download_provider_name") != "") {
        accordionHtml += `  data-mf-download_provider_name="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "download_provider_name")}" `;
        accordionHtml += `  data-mf-download_provider_name-namespace="cm_digital" `;
      }
      if (this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "isbn") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "isbn") != "") {
        accordionHtml += `  data-mf-isbn="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "isbn")}" `;
        accordionHtml += `  data-mf-isbn-namespace="cm_digital" `;
      }
      if (this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "refundable")) {
        accordionHtml += `  data-mf-refundable="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "refundable")}" `;
        accordionHtml += `  data-mf-refundable-namespace="cm_digital" `;
      }
      if (this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "partial_content") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "partial_content") != "") {
        accordionHtml += `  data-mf-partial_content="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "partial_content")}" `;
        accordionHtml += `  data-mf-partial_content-namespace="cm_digital" `;
      }
      if (this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "print_limit") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "print_limit") != "") {
        accordionHtml += `  data-mf-print_limit="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "print_limit")}" `;
        accordionHtml += `  data-mf-print_limit-namespace="cm_digital" `;
      }
      if (this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "access") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "access") != "") {
        accordionHtml += `  data-mf-access="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "access")}" `;
        accordionHtml += `  data-mf-access-namespace="cm_digital" `;
      }
      if (subscriptionStr) {
        accordionHtml += `  data-mf-subscription="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "subscription")}" `;
        accordionHtml += `  data-mf-subscription-namespace="cm_digital" `;
      }
      accordionHtml += `  > `;
      accordionHtml += `  Requirements `;
      accordionHtml += `  </button> `;
      accordionHtml += `  </div> `;
      if (subscription > 0) {
        accordionHtml += `  <div class="accordion-variant-rental-due"> `;
        accordionHtml += `  <span class="accordion-variant-rental-due-text">${digitalRentalDurationLabel}</span> `;
        accordionHtml += `  <span class="accordion-variant-rental-due-date">${formatDigitalRentalDaysForPicker(subscription)}</span> `;
        accordionHtml += `  </div> `;
      }
      if (variantDigital.available) {
        accordionHtml += `  </label> `;
      }
      if (variantDigital.available === false) {
        accordionHtml += `  <div class="purchase-price out-of-stock-overlay"> Out of Stock </div> `;
      }
      accordionHtml += `  </div> `;
    }
    accordionHtml += ` </div> `;
    accordionHtml += ` <div class="digital-delivery-notice">*Digital delivery fees may apply</div> `;
    accordionHtml += ` </div> `;
    
    
    div.innerHTML = accordionHtml;
    const tabs = document.querySelector('.format-tabs');
    tabs.after(div);
    

    if(rentLabel){
      const divRent = document.createElement('div');
      divRent.setAttribute('class', 'rental-banner');
      let rentLabelHTML = ` <span class="dollar-icon">$</span>
                            Rent for the semester and save money! `;
      divRent.innerHTML = rentLabelHTML;

      const contentDigital = document.querySelectorAll('.content-digital');      
      contentDigital[0].prepend(divRent);

    }

    const formatCollapse = document.querySelectorAll('.format-tab-panel');

    formatCollapse.forEach(element => {
      if (element.getAttribute('data-format') === 'print') {
        element.classList.remove('active');
        element.style.display = 'none';
      }
    });

    const formatTab = document.querySelectorAll('.format-tab');
    formatTab.forEach(element => {
      if (element.getAttribute('data-format') === 'print') {
        element.classList.remove('active');
      }
    });


  }

  renderDigitalProductsUnifiedDialog(digitalProducts){
    
    let rentLabel = false;
    const modalContent = document.getElementById('quick-add-modal-content');
    let purchaseTypesContainer = modalContent?.querySelector('.format-collapse[data-format="digital"] .purchase-types') || document.querySelector('.format-collapse[data-format="digital"] .purchase-types');
    if (!purchaseTypesContainer) purchaseTypesContainer = modalContent?.querySelector('.format-collapse.active .collapse-content .purchase-types') || document.querySelector('.format-collapse.active .collapse-content .purchase-types');
    const allUnifiedItems = [];
    const normalizePriceFromDom = (val) => {
      const n = parseFloat(val);
      if (isNaN(n)) return 0;
      if (Number.isInteger(n) && n >= 1000) return n / 100;
      return n;
    };
    if (purchaseTypesContainer) {
      const existingEls = Array.from(purchaseTypesContainer.querySelectorAll(':scope > .purchase-type'));
      existingEls.forEach((el) => {
        const typeStr = (el.querySelector('[data-type]')?.getAttribute('data-type')) || el.getAttribute('data-purchase-type') || (el.querySelector('.purchase-type-label')?.textContent?.trim()) || '';
        const priceStr = el.querySelector('input[data-price]')?.getAttribute('data-price');
        let priceNum = 0;
        if (priceStr) priceNum = normalizePriceFromDom(priceStr);
        else {
          const priceEl = el.querySelector('.purchase-price:not(.out-of-stock-overlay)');
          const raw = priceEl && (priceEl.textContent || '').trim().match(/[\d,.]+/);
          if (raw) priceNum = parseFloat(raw[0].replace(/,/g, '')) || 0;
        }
        allUnifiedItems.push({ variant: { option2: typeStr, price: priceNum }, productTitle: el.querySelector('[data-product-title]')?.getAttribute('data-product-title') || '', existingEl: el });
      });
    }
    for (let i = 0; i < digitalProducts.length; i++) {
      const digitalProduct = digitalProducts[i].variants || [];
      const productTitle = digitalProducts[i].title;
      for (let v = 0; v < digitalProduct.length; v++) {
        allUnifiedItems.push({ variant: digitalProduct[v], productTitle, existingEl: null });
      }
    }
    allUnifiedItems.sort((a, b) => this.sortDigitalVariants(a.variant, b.variant));
    if (purchaseTypesContainer) {
      while (purchaseTypesContainer.firstChild) purchaseTypesContainer.removeChild(purchaseTypesContainer.firstChild);
    }
    for (let d = 0; d < allUnifiedItems.length; d++) {
        const item = allUnifiedItems[d];
        if (item.existingEl) {
          if (purchaseTypesContainer) purchaseTypesContainer.appendChild(item.existingEl);
          if (item.variant.option2 && item.variant.option2.toLowerCase().includes('rent')) rentLabel = true;
          continue;
        }
        let accordionHtml = ` `;
        const variantDigital = item.variant;
        const productTitle = item.productTitle;
        const availabilityClass = variantDigital.available ? '' : ' unavailable';
        const variantLabel = variantDigital.option2;
        if(variantDigital.option2.toLowerCase().includes("rent")){
          rentLabel = true;
        }

        const div = document.createElement('div');
        div.setAttribute('class', 'purchase-type '+ availabilityClass);
        div.setAttribute('data-purchase-type', variantDigital.option2.toLowerCase());
        div.setAttribute('data-variant-id', variantDigital.id.split('/').pop());
        div.setAttribute('data-price', String(variantDigital.price));
        
        accordionHtml += `  <div class="purchase-type-header">
                              <div class="purchase-type-label-container">
                                  <span class="purchase-type-label">
                                    ${variantLabel}
                                  </span> ` ;
       
        accordionHtml += `    </div> `;
        if(variantDigital.available){
          accordionHtml += `  <div class="purchase-checkbox-container" onclick="event.stopPropagation()"> `;
          accordionHtml += `  <input `;
          accordionHtml += `  type="checkbox" `;
          accordionHtml += `  id="variant-${variantDigital.id.split('/').pop()}" `;
          accordionHtml += `  name="purchase-checkbox-${variantDigital.id.split('/').pop()}" `;
          accordionHtml += `  class="purchase-checkbox" `;
          accordionHtml += `  data-format="${variantDigital.option1}" `;
          accordionHtml += `  data-type="${variantDigital.option2}" `;
          accordionHtml += `  data-is-digital="true" `;
          accordionHtml += `  data-publisher-code="${this.getMetafieldValueDialog(variantDigital.metafields, "course_materials", "publisher_code")}" `;
          accordionHtml += `  data-variant-id="${variantDigital.id.split('/').pop()}" `;
          accordionHtml += `  data-price="${variantDigital.price}" `;
          accordionHtml += `  data-is-rental="false" `;
          accordionHtml += `  data-book-rent-due-date="" `;
          accordionHtml += `  data-non-return-processing-fee="0" `;
          accordionHtml += `  data-non-return-charge="0" `;
          accordionHtml += `  data-option-value-id="" `;
          accordionHtml += `  data-option-available="${variantDigital.available}" `;
          accordionHtml += `  data-variant-title="${productTitle} ${variantDigital.option1} / ${variantDigital.option2}" `;
          accordionHtml += `  data-department="${this.getMetafieldValueDialog(variantDigital.metafields, "taxonomy", "department")}" `;
          accordionHtml += `  data-sub-department="${this.getMetafieldValueDialog(variantDigital.metafields, "taxonomy", "sub_department")}" `;
          accordionHtml += `  data-class="${this.getMetafieldValueDialog(variantDigital.metafields, "taxonomy", "class")}" `;
          accordionHtml += `  data-sub-class="${this.getMetafieldValueDialog(variantDigital.metafields, "taxonomy", "sub_class")}" `;
          accordionHtml += `  data-rental-only-sku="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_rental", "rental_only_sku")}"  `;
          accordionHtml += `  data-book-isbn="${this.getMetafieldValueDialog(variantDigital.metafields, "course_materials", "isbn_ean")}"  `;
          accordionHtml += `  data-book-edition="${this.getMetafieldValueDialog(variantDigital.metafields, "course_materials", "edition")}"  `;
          accordionHtml += `  data-subscription-length="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "subscription")}"  `;
          
          accordionHtml += `  > `;
          accordionHtml += `  <label for="variant-${variantDigital.id.split('/').pop()}" class="checkbox-label"></label> `;
          accordionHtml += `  </div> `;
        }
          
        accordionHtml += `  </div> `;  
        const formattedPrice = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(parseFloat(variantDigital.price));
         const digitalProd = JSON.parse(document.getElementById("digitalProd").value);
          accordionHtml += `  <div class="purchase-type-tooltip"> `;
        if(digitalProd){
          const textDigital = document.getElementById("digital_fee_notice_text")?.value || "";
          accordionHtml += `  <span class="tooltip-trigger" data-tooltip="${textDigital}">  `;
          accordionHtml += `     <svg class="question-icon" width="16" height="16" viewBox="0 0 16 16" fill="none"> `;
          accordionHtml += `     <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/> `;
          accordionHtml += `     <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/> `;
          accordionHtml += `     </svg> `;
          accordionHtml += `  </span> `;          
        }
        accordionHtml += `  </div> `;
        accordionHtml += `  <div class="purchase-price">${formattedPrice}</div> `;          
        accordionHtml += `  <div class="" data-format-specific="digital"> `;
        accordionHtml += `  <button  `;
        accordionHtml += `  type="button"  `;
        accordionHtml += `  class="requirements-link"  `;
        accordionHtml += `  data-variant-id="${variantDigital.id.split('/').pop()}" `;
        accordionHtml += `  data-product-title="${productTitle}" `;
        accordionHtml += `  data-format="${variantDigital.option1}" `;
        accordionHtml += `  data-type="${variantDigital.option2}" `;          
        if(this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "is_digital") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "is_digital") != ""){
          accordionHtml += `  data-mf-is_digital="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "is_digital")}" `;
          accordionHtml += `  data-mf-is_digital-namespace="cm_digital" `;
        }            
        if(this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "publisher_name") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "publisher_name") != ""){              
          accordionHtml += `  data-mf-publisher_name="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "publisher_name")}" `;
          accordionHtml += `  data-mf-publisher_name-namespace="cm_digital" `;
        }
        if(this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "ebook_format") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "ebook_format") != ""){
          accordionHtml += `  data-mf-ebook_format="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "ebook_format")}" `;
          accordionHtml += `  data-mf-ebook_format-namespace="cm_digital" `;
        }
        if(this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "download_provider_name") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "download_provider_name") != ""){
          accordionHtml += `  data-mf-download_provider_name="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "download_provider_name")}" `;
          accordionHtml += `  data-mf-download_provider_name-namespace="cm_digital" `;
        }
        if(this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "isbn") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "isbn") != ""){
          accordionHtml += `  data-mf-isbn="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "isbn")}" `;
          accordionHtml += `  data-mf-isbn-namespace="cm_digital" `;
        }
        if(this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "refundable") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "refundable")!= ""){
          accordionHtml += `  data-mf-refundable="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "refundable")}" `;
          accordionHtml += `  data-mf-refundable-namespace="cm_digital" `;
        
        }
        if(this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "partial_content") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "partial_content") != ""){          
          accordionHtml += `  data-mf-partial_content="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "partial_content")}" `;
          accordionHtml += `  data-mf-partial_content-namespace="cm_digital" `;          
        }
        if(this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "print_limit") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "print_limit") != ""){          
          accordionHtml += `  data-mf-print_limit="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "print_limit")}" `;
          accordionHtml += `  data-mf-print_limit-namespace="cm_digital"  `;         
        }
        if(this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "access") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "access") != ""){          
          accordionHtml += `  data-mf-access="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "access")}" `;
          accordionHtml += `  data-mf-access-namespace="cm_digital"  `;         
        }
        let subscription = 0;
        if(this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "subscription") && this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "subscription") != ""){          
          subscription = parseInt(this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "subscription"));  
          accordionHtml += `  data-mf-subscription="${this.getMetafieldValueDialog(variantDigital.metafields, "cm_digital", "subscription")}" `;
          accordionHtml += `  data-mf-subscription-namespace="cm_digital" `;          
        }
        accordionHtml += `  > `;
        accordionHtml += `  Requirements `;
        accordionHtml += `  </button> `;
        if(subscription > 0){
          
          accordionHtml += `  <div class="accordion-variant-rental-due"> `;
          accordionHtml += `    <span class="accordion-variant-rental-due-text"> `;          
          accordionHtml += `        Rental duration: `;
          accordionHtml += `    </span> `;
          accordionHtml += `    <span class="accordion-variant-rental-due-date" > `;
          accordionHtml += `      ${subscription} days. `;
          accordionHtml += `    </span> `;
          accordionHtml += `  </div> `;

        }
        
        accordionHtml += `  </div> `;

        if(variantDigital.available == false){
          accordionHtml += `    <div class="purchase-price out-of-stock-overlay"> Out of Stock </div> `;
        }
        
        div.innerHTML = accordionHtml;
        const container = purchaseTypesContainer || document.querySelector('.format-collapse.active .collapse-content .purchase-types');
        if (container) container.append(div);
      }
  }
  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    document.body.style.width = '100%';
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());
    
    // Clear flags when modal opens to ensure fresh state
    const modalContent = document.getElementById('quick-add-modal-content');
    if (modalContent) {
      modalContent.removeAttribute('data-variants-rendered');
      modalContent.removeAttribute('data-adding-to-cart');
    }
    if (typeof setupQuickAddTitlePriceOnSelect === 'function') setupQuickAddTitlePriceOnSelect();

    const textbookVariantPicker = document.querySelectorAll('.product-details.sticky-content--desktop');
    const quickViewDialog = document.querySelectorAll(".quick-add-modal");
    
    // Check if variants have already been rendered or if items are being added to cart
    const variantsAlreadyRendered = modalContent?.hasAttribute('data-variants-rendered');
    const isAddingToCart = modalContent?.hasAttribute('data-adding-to-cart');
    
    if (textbookVariantPicker && textbookVariantPicker.length > 0 && quickViewDialog && quickViewDialog.length > 0 && dialog.className != "requirements-dialog-element") {
      quickViewDialog.forEach(async dialog => {    
        if (dialog.hasAttribute("open") && !variantsAlreadyRendered && !isAddingToCart) {        
          window.markerConfig = {
            project: '68dc3627f73470e251e36f26', 
            source: 'snippet'
          };
          if (window.shopifySettings && window.shopifySettings.unifiedViewQuickView) {
            let isPrintProduct = false;
            let isDigitalProduct = false;
            const elements = document.querySelectorAll('.details-pdp.accordion-variant-picker-pdp');
            if(elements){
              for (const element of elements) {
                const testId = element.getAttribute('data-testid'); 
                if (testId === 'accordion-variant-picker-print') {
                  isPrintProduct = true;
                  break; 
                }
                if (testId === 'accordion-variant-picker-digital') {
                  isDigitalProduct = true;
                  break; 
                }
              }
            }
            if(isPrintProduct){
              const isbnCode = document.getElementById("isbn_ean").value;
              const digitalProducts = await this.fetchPrintDigitalProductsDialog("metafields.course_materials.print_digital_isbn_relation:\""+isbnCode+"\"");

              if(digitalProducts && digitalProducts.length > 0){
                this.renderDigitalProductsDialog(digitalProducts);

                // Selecionar o botão pelo seu data-testid
                const button = document.querySelector('[data-testid="accordion-variant-picker-print"]');

                // Alterar o valor do atributo onclick
                if (button) {
                    button.setAttribute('onclick', '');
                }
                      
              }
            }
            if(isDigitalProduct){      
              const print_digital_isbn_relation = document.getElementById("print_digital_isbn_relation").value; 
              if(print_digital_isbn_relation && print_digital_isbn_relation != ''){
                
                const isbns_print = JSON.parse(print_digital_isbn_relation);
                
                if(isbns_print && isbns_print.length > 0){
                  let isbnQuery = ` `;
                  let isbnConditions = [];
                  for (let isbnCode of isbns_print) {

                    let isnb_value = isbnCode;        
                    if (!isnb_value || isnb_value == '') {
                      continue;
                    }
                    isbnConditions.push(`metafields.course_materials.isbn_ean:'${isnb_value}'`);

                  }
                  if (isbnConditions.length > 0) {
                    isbnQuery = `(${isbnConditions.join(" OR ")})`;
                    const printProducts = await this.fetchPrintDigitalProductsDialog(isbnQuery);

                    if(printProducts && printProducts.length > 0){
                      //APPLY PRINT DISPLAY RULES
                      for (let index = 0; index < printProducts.length; index++) {
                        printProducts[index].variants = this.applyPrintDisplayRulesDialog(printProducts[index], "Print") ;
                      }
                      this.renderPrintProductsDialog(printProducts);
                      // Selecionar o botão pelo seu data-testid
                      const button = document.querySelector('[data-testid="accordion-variant-picker-digital"]');
                      // Alterar o valor do atributo onclick
                      if (button) {
                        button.setAttribute('onclick', '');
                      }      
                    }
                  }

                }
                

              }

              const isbnCode = document.getElementById("isbn_ean").value;
              const productId = document.getElementById("productId").value;
              const digitalProductsUnified = await this.fetchPrintDigitalProductsDialog("metafields.course_materials.isbn_ean:\""+isbnCode+"\"AND NOT id:\""+productId+"\"");
              if(digitalProductsUnified && digitalProductsUnified.length > 0){
                this.renderDigitalProductsUnifiedDialog(digitalProductsUnified);
              }
            }
            const pricesByFormat = { digital: [], print: [] };
            const seen = { digital: new Set(), print: new Set() };
            const normalizeFromDataPrice = (val) => {
              const n = parseFloat(val);
              if (isNaN(n)) return null;
              if (Number.isInteger(n) && n >= 1000) return n / 100;
              return n;
            };
            (modalContent?.querySelectorAll('.purchase-type') || []).forEach((el) => {
              const collapse = el.closest('.format-collapse[data-format]');
              const format = collapse?.getAttribute('data-format');
              if (format !== 'digital' && format !== 'print') return;
              let price = null;
              const mt = el.getAttribute('data-price');
              if (mt != null && mt !== '') {
                price = normalizeFromDataPrice(mt);
              } else {
                const withPrice = el.querySelector('[data-price]');
                if (withPrice) price = normalizeFromDataPrice(withPrice.getAttribute('data-price'));
                else {
                  const priceEls = el.querySelectorAll('.purchase-price:not(.out-of-stock-overlay)');
                  for (const pe of priceEls) {
                    const raw = pe.textContent?.trim().match(/[\d,.]+/);
                    if (raw) { price = parseFloat(raw[0].replace(/,/g, '')); break; }
                  }
                }
              }
              if (price != null && !isNaN(price) && !seen[format].has(price)) {
                seen[format].add(price);
                pricesByFormat[format].push(price);
              }
            });
            ['digital', 'print'].forEach((k) => { pricesByFormat[k].sort((a, b) => a - b); });
            const formatPrice = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
            const allPrices = [...pricesByFormat.digital, ...pricesByFormat.print];
            const lowest = allPrices.length ? Math.min(...allPrices) : null;
            const highest = allPrices.length ? Math.max(...allPrices) : null;
            updateQuickAddTitlePrice(modalContent, { formatPrice, lowest, highest, normalizeFromDataPrice });
            const tabDigital = modalContent?.querySelector('[data-testid="accordion-variant-picker-digital"]');
            const tabPrint = modalContent?.querySelector('[data-testid="accordion-variant-picker-print"]');
            const minDigital = pricesByFormat.digital.length ? Math.min(...pricesByFormat.digital) : null;
            const minPrint = pricesByFormat.print.length ? Math.min(...pricesByFormat.print) : null;
            [tabDigital, tabPrint].forEach((tab, i) => {
              if (!tab) return;
              const min = i === 0 ? minDigital : minPrint;
              let sp = tab.querySelector('.format-tab-price');
              if (min != null) {
                if (!sp) { sp = document.createElement('span'); sp.className = 'format-tab-price'; tab.appendChild(sp); }
                sp.textContent = ` From ${formatPrice(min)}`;
              } else if (sp) sp.remove();
            });
          }
          
          // Mark that variants have been rendered to prevent duplicates
          if (modalContent && !modalContent.hasAttribute('data-variants-rendered')) {
            modalContent.setAttribute('data-variants-rendered', 'true');
          }
          
          this.disableAddToBagButtonDialog();
          this.textBookFuncs();
          this.setupRequirementsButtonsDialog();
          this.setupAddToBagMultipleDialog();
          this.updateRentalDatesDialog();  
          
          

          const buttons = document.querySelectorAll('.accordion-variant-picker-pdp');

          buttons.forEach(button => {
            button.addEventListener('click', function() {
              const container = button.closest('.purchase-options-container');
              if (!container) return;

              const format = button.dataset.format;
              const isActive = button.classList.contains('active');
              
              // If clicking on an already active tab, do nothing
              if (isActive) return;
              
              // Update tab states
              const allTabs = container.querySelectorAll('.format-tab');
              allTabs.forEach(tab => {
                tab.classList.remove('active');
              });
              button.classList.add('active');
              
              // Update collapse states
              const allCollapses = container.querySelectorAll('.format-collapse');
              allCollapses.forEach(c => {
                c.classList.remove('active');
                c.style.display = 'none';
              });
              
              // Show the selected format collapse
              const targetCollapse = container.querySelector(`.format-collapse[data-format="${format}"]`);
              if (targetCollapse) {
                targetCollapse.classList.add('active');
                targetCollapse.style.display = 'block';
                //this.applySmartPickLabels(targetCollapse);
              } else {
                console.error('targetCollapse not found for format:', format);
              }
            });
          });
        } 
      });
      
    }else{
      return;
    }
    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // Clear flags when dialog closes so variants can be rendered again on next open
    const modalContent = document.getElementById('quick-add-modal-content');
    if (modalContent) {
      modalContent.removeAttribute('data-variants-rendered');
      modalContent.removeAttribute('data-adding-to-cart');
    }

    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}



    
document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

/**
 * Updates the Quick Add modal title price: full range (low–high) by default,
 * or the single variant price when exactly one variant is selected.
 * @param {HTMLElement} modalContent - #quick-add-modal-content
 * @param {{ formatPrice: (n: number) => string, lowest: number, highest: number, normalizeFromDataPrice: (v: string) => number | null }} [precomputed] - optional, from initial variant render
 */
function updateQuickAddTitlePrice(modalContent, precomputed) {
  const priceContainer = modalContent?.querySelector('product-price [ref="priceContainer"]');
  if (!priceContainer) return;
  const normalizeFromDataPrice = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return null;
    if (Number.isInteger(n) && n >= 1000) return n / 100;
    return n;
  };
  const formatPrice = (n) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
  const formatPriceWithCents = (n) => {
    const s = formatPrice(n);
    const m = s.match(/^(\$[\d,]*)(\.)(\d{2})$/);
    return m ? m[1] + m[2] + '<span class="price-cents">' + m[3] + '</span>' : s;
  };
  let lowest, highest;
  if (precomputed && precomputed.lowest != null && precomputed.highest != null) {
    lowest = precomputed.lowest;
    highest = precomputed.highest;
  } else {
    const pricesByFormat = { digital: [], print: [] };
    const seen = { digital: new Set(), print: new Set() };
    (modalContent?.querySelectorAll('.purchase-type') || []).forEach((el) => {
      const collapse = el.closest('.format-collapse[data-format]');
      const format = collapse?.getAttribute('data-format');
      if (format !== 'digital' && format !== 'print') return;
      let price = null;
      const mt = el.getAttribute('data-price');
      if (mt != null && mt !== '') {
        price = normalizeFromDataPrice(mt);
      } else {
        const withPrice = el.querySelector('[data-price]');
        if (withPrice) price = normalizeFromDataPrice(withPrice.getAttribute('data-price'));
        else {
          const priceEls = el.querySelectorAll('.purchase-price:not(.out-of-stock-overlay)');
          for (const pe of priceEls) {
            const raw = pe.textContent?.trim().match(/[\d,.]+/);
            if (raw) { price = parseFloat(raw[0].replace(/,/g, '')); break; }
          }
        }
      }
      if (price != null && !isNaN(price) && !seen[format].has(price)) {
        seen[format].add(price);
        pricesByFormat[format].push(price);
      }
    });
    const allPrices = [...(pricesByFormat.digital || []), ...(pricesByFormat.print || [])];
    lowest = allPrices.length ? Math.min(...allPrices) : null;
    highest = allPrices.length ? Math.max(...allPrices) : null;
  }
  if (lowest == null || highest == null) return;
  const checked = modalContent.querySelectorAll('.purchase-checkbox:checked');
  if (checked.length === 1) {
    const cb = checked[0];
    const pt = cb.closest('.purchase-type');
    const norm = precomputed?.normalizeFromDataPrice ?? normalizeFromDataPrice;
    let price = null;
    const mt = pt?.getAttribute('data-price');
    if (mt != null && mt !== '') {
      price = norm(mt);
    } else {
      const dp = pt?.querySelector('[data-price]');
      if (dp) price = norm(dp.getAttribute('data-price'));
      if (price == null) {
        const pe = pt?.querySelector('.purchase-price:not(.out-of-stock-overlay)');
        const raw = pe?.textContent?.trim().match(/[\d,.]+/);
        if (raw) price = parseFloat(raw[0].replace(/,/g, ''));
      }
    }
    if (price != null && !isNaN(price)) {
      priceContainer.innerHTML = `<span class="price-range">${formatPriceWithCents(price)}</span>`;
      return;
    }
  }
  const range =
    lowest === highest
      ? formatPriceWithCents(lowest)
      : `${formatPriceWithCents(lowest)} - ${formatPriceWithCents(highest)}`;
  priceContainer.innerHTML = `<span class="price-range">${range}</span>`;
}

// Expose so quick-add.js can call when modal content is first loaded (range on open)
if (typeof window !== 'undefined') window.updateQuickAddTitlePrice = updateQuickAddTitlePrice;

function setupQuickAddTitlePriceOnSelect() {
  const content = document.getElementById('quick-add-modal-content');
  if (!content || content.hasAttribute('data-title-price-setup')) return;
  content.setAttribute('data-title-price-setup', '1');
  content.addEventListener('change', (e) => {
    if (e.target?.type === 'checkbox' && e.target.classList.contains('purchase-checkbox')) {
      updateQuickAddTitlePrice(content);
    }
  });
}

function prefersHoverTooltip() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
}

/**
 * Quick Add modal: tooltip portal lives inside .collapse-content, centered.
 * On show, move portal into trigger's collapse-content; on hide, move back to dialog.
 */
function setupQuickAddTooltipPortal() {
  const portal = document.getElementById('quick-add-tooltip-portal');
  const content = document.getElementById('quick-add-modal-content');
  const modal = document.querySelector('.quick-add-modal');
  if (!portal || !content || !modal) return;
  if (content.hasAttribute('data-tooltip-portal-setup')) return;
  content.setAttribute('data-tooltip-portal-setup', '1');

  let currentTrigger = null;

  function hide() {
    currentTrigger = null;
    portal.setAttribute('hidden', '');
    portal.setAttribute('aria-hidden', 'true');
    if (portal.parentNode && portal.parentNode !== modal) {
      modal.appendChild(portal);
    }
  }

  function show(trigger) {
    const text = trigger.getAttribute('data-tooltip');
    if (!text) return;
    const collapseContent = trigger.closest('.collapse-content');
    if (!collapseContent) return;
    currentTrigger = trigger;
    if (portal.parentNode !== collapseContent) {
      collapseContent.appendChild(portal);
    }
    portal.textContent = text;
    portal.removeAttribute('hidden');
    portal.setAttribute('aria-hidden', 'false');
  }

  content.addEventListener('mouseover', (e) => {
    if (!modal.hasAttribute('open')) return;
    if (!prefersHoverTooltip()) return;
    const t = e.target.closest('.tooltip-trigger');
    if (t) show(t);
  });

  content.addEventListener('mouseout', (e) => {
    if (!prefersHoverTooltip()) return;
    const t = e.target.closest('.tooltip-trigger');
    const r = e.relatedTarget;
    if (t && (!r || !t.contains(r))) hide();
  });

  content.addEventListener('click', (e) => {
    if (!modal.hasAttribute('open')) return;
    if (prefersHoverTooltip()) return;
    const t = e.target.closest('.tooltip-trigger');
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();
    const isOpen = !portal.hasAttribute('hidden');
    if (isOpen && currentTrigger === t) {
      hide();
    } else {
      show(t);
    }
  });

  document.addEventListener('click', (e) => {
    if (prefersHoverTooltip()) return;
    if (!modal.hasAttribute('open')) return;
    if (portal.hasAttribute('hidden')) return;
    if (portal.contains(e.target)) return;
    if (e.target.closest('.quick-add-modal .tooltip-trigger')) return;
    hide();
  });

  document.addEventListener('dialog:close', () => {
    if (document.querySelector('.quick-add-modal[open]')) return;
    hide();
  });

  modal.addEventListener('close', hide);
}

/**
 * PDP: tooltip portal anchored to the container div that has .collapse-content.
 * Uses document.querySelector('div:has(.collapse-content)') as the anchor,
 * and lays the portal centered at the top of that content area.
 */
function setupPDPTooltipPortal() {
  const portal = document.getElementById('pdp-tooltip-portal');
  const pdpContainer = document.querySelector('.material-purchase-options-pdp');
  if (!portal || !pdpContainer) return;
  if (pdpContainer.hasAttribute('data-pdp-tooltip-portal-setup')) return;
  pdpContainer.setAttribute('data-pdp-tooltip-portal-setup', '1');

  // Anchor element: first div that contains a .collapse-content, scoped to PDP container
  const anchor =
    pdpContainer.querySelector('div:has(.collapse-content)') ||
    document.querySelector('div:has(.collapse-content)');

  if (!anchor) return;

  // Ensure anchor can contain absolutely positioned portal
  const anchorStyle = window.getComputedStyle(anchor);
  if (anchorStyle.position === 'static') {
    anchor.style.position = 'relative';
  }

  if (portal.parentNode !== anchor) {
    anchor.appendChild(portal);
  }

  let currentTrigger = null;

  function hide() {
    currentTrigger = null;
    portal.setAttribute('hidden', '');
    portal.setAttribute('aria-hidden', 'true');
  }

  function show(trigger) {
    const text = trigger.getAttribute('data-tooltip');
    if (!text) return;
    currentTrigger = trigger;
    portal.textContent = text;
    portal.removeAttribute('hidden');
    portal.setAttribute('aria-hidden', 'false');
  }

  pdpContainer.addEventListener(
    'click',
    (e) => {
      const t = e.target.closest('.tooltip-trigger');
      if (!t || !pdpContainer.contains(t)) return;
      e.preventDefault();
    },
    true
  );

  pdpContainer.addEventListener('mouseover', (e) => {
    if (!prefersHoverTooltip()) return;
    const t = e.target.closest('.tooltip-trigger');
    if (t) show(t);
  });

  pdpContainer.addEventListener('mouseout', (e) => {
    if (!prefersHoverTooltip()) return;
    const t = e.target.closest('.tooltip-trigger');
    const r = e.relatedTarget;
    if (
      t &&
      (
        !r ||
        (
          !r.closest('.material-purchase-options-pdp .tooltip-trigger') &&
          !portal.contains(r)
        )
      )
    ) {
      hide();
    }
  });

  pdpContainer.addEventListener('click', (e) => {
    if (prefersHoverTooltip()) return;
    const t = e.target.closest('.tooltip-trigger');
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();

    const isOpen = !portal.hasAttribute('hidden');
    if (isOpen && currentTrigger === t) {
      hide();
    } else {
      show(t);
    }
  });

  document.addEventListener('click', (e) => {
    if (prefersHoverTooltip()) return;
    if (portal.hasAttribute('hidden')) return;
    if (portal.contains(e.target)) return;
    if (e.target.closest('.material-purchase-options-pdp .tooltip-trigger')) return;
    hide();
  });
}

/**
 * CMRP / discovery: portal for hover/click help still using `.tooltip-trigger` / `[data-tooltip]`
 * under `.material-purchase-options` (e.g. access-program copy). Purchase variant messages are
 * not tooltips: they use `data-variant-notice` on `.purchase-type` and render as `.variant-notice`
 * under `.collapse-content` (see syncCmrpVariantNoticeInCollapse in material-interaction-manager.js).
 */
const CMRP_TOOLTIP_SELECTOR = '.material-purchase-options:not(.material-purchase-options-pdp) .tooltip-trigger, .material-purchase-options:not(.material-purchase-options-pdp) [data-tooltip]';

function setupCMRPTooltipPortal() {
  if (document.body.hasAttribute('data-cmrp-tooltip-portal-setup')) return;
  document.body.setAttribute('data-cmrp-tooltip-portal-setup', '1');

  let portal = document.getElementById('cmrp-tooltip-portal');
  if (!portal) {
    portal = document.createElement('div');
    portal.id = 'cmrp-tooltip-portal';
    portal.className = 'cmrp-tooltip-portal';
    portal.setAttribute('hidden', '');
    portal.setAttribute('aria-hidden', 'true');
    document.body.appendChild(portal);
  }

  let currentTrigger = null;
  let currentContainer = null;
  let scrollListener = null;
  let windowScrollListener = null;
  let resizeListener = null;
  const hasHover =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function updatePortalPosition() {
    if (!currentContainer || portal.hasAttribute('hidden')) return;
    const rect = currentContainer.getBoundingClientRect();
    portal.style.position = 'fixed';
    portal.style.left = `${rect.left + rect.width / 2}px`;
    portal.style.top = `${rect.top + rect.height / 2}px`;
    portal.style.transform = 'translate(-50%, -50%)';
  }

  function hide() {
    if (scrollListener && currentContainer) {
      currentContainer.removeEventListener('scroll', scrollListener);
      scrollListener = null;
    }
    if (windowScrollListener) {
      window.removeEventListener('scroll', windowScrollListener, true);
      windowScrollListener = null;
    }
    if (resizeListener) {
      window.removeEventListener('resize', resizeListener);
      resizeListener = null;
    }
    currentTrigger = null;
    currentContainer = null;
    portal.removeAttribute('style');
    portal.setAttribute('hidden', '');
    portal.setAttribute('aria-hidden', 'true');
  }

  function show(trigger) {
    const text = trigger.getAttribute('data-tooltip');
    if (!text) return;
    const container = trigger.closest('.collapse-content') || trigger.closest('.material-purchase-options');
    if (!container) return;
    if (scrollListener && currentContainer && currentContainer !== container) {
      currentContainer.removeEventListener('scroll', scrollListener);
      scrollListener = null;
    }
    currentTrigger = trigger;
    currentContainer = container;
    if (portal.parentNode !== document.body) {
      document.body.appendChild(portal);
    }
    portal.textContent = text;
    portal.removeAttribute('hidden');
    portal.setAttribute('aria-hidden', 'false');
    updatePortalPosition();
    if (!scrollListener) {
      scrollListener = () => updatePortalPosition();
      currentContainer.addEventListener('scroll', scrollListener, { passive: true });
    }
    if (!windowScrollListener) {
      windowScrollListener = () => updatePortalPosition();
      window.addEventListener('scroll', windowScrollListener, true);
    }
    if (!resizeListener) {
      resizeListener = () => updatePortalPosition();
      window.addEventListener('resize', resizeListener);
    }
  }

  // Only use hover on devices that support it; on touch devices synthetic mouse events
  // after a tap cause the tooltip to open then immediately close (flicker) and then seem unresponsive.
  if (hasHover) {
    document.addEventListener('mouseover', (e) => {
      const t = e.target.closest(CMRP_TOOLTIP_SELECTOR);
      if (t) show(t);
    });

    document.addEventListener('mouseout', (e) => {
      const t = e.target.closest(CMRP_TOOLTIP_SELECTOR);
      const r = e.relatedTarget;
      if (
        t &&
        currentTrigger &&
        (!r ||
          (!r.closest(CMRP_TOOLTIP_SELECTOR) &&
            !portal.contains(r)))
      ) {
        hide();
      }
    });
  }

  let lastTouchTrigger = null;
  let lastTouchShowTime = 0;
  document.addEventListener('touchend', (e) => {
    const t = e.target.closest(CMRP_TOOLTIP_SELECTOR);
    if (t) {
      lastTouchTrigger = t;
      const isOpen = !portal.hasAttribute('hidden');
      if (isOpen && currentTrigger === t) hide();
      else {
        show(t);
        lastTouchShowTime = Date.now();
      }
      setTimeout(() => { lastTouchTrigger = null; }, 350);
    }
  }, { passive: true });

  document.addEventListener('click', (e) => {
    const t = e.target.closest(CMRP_TOOLTIP_SELECTOR);
    if (!t) return;
    e.stopPropagation();
    if (lastTouchTrigger === t) return;
    const isOpen = !portal.hasAttribute('hidden');
    if (hasHover) {
      if (isOpen && currentTrigger === t) hide();
      else show(t);
    } else {
      if (isOpen && currentTrigger === t) hide();
      else show(t);
    }
  });

  document.addEventListener('click', (e) => {
    if (portal.hasAttribute('hidden')) return;
    if (portal.contains(e.target)) return;
    if (e.target.closest(CMRP_TOOLTIP_SELECTOR)) return;
    // On touch devices, ignore the synthetic click that often fires ~300ms after touch
    // so it doesn't immediately close the tooltip we just opened.
    if (!hasHover && lastTouchShowTime && (Date.now() - lastTouchShowTime) < 400) return;
    hide();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setupQuickAddTooltipPortal();
    setupQuickAddTitlePriceOnSelect();
    setupPDPTooltipPortal();
    setupCMRPTooltipPortal();
  });
} else {
  setupQuickAddTooltipPortal();
  setupQuickAddTitlePriceOnSelect();
  setupPDPTooltipPortal();
  setupCMRPTooltipPortal();
}
